/**
 * Finance Budgets screen (FIN-12) — two views in one page:
 *
 *   Tab "Edit budgets" — month navigator (← YYYY-MM →); expense-category tree
 *     with leaf inputs and parent sums; explicit Save bar; "Copy from previous
 *     month" when the current month has no budgets.
 *
 *   Tab "vs Actual" — month navigator; hierarchical table (category, budget,
 *     actual, variance, progress bar) from GET /analytics/budget-vs-actual;
 *     expandable parents; rows with spend-but-no-budget are visually flagged.
 *
 * Design constraints:
 *   - Only EXPENSE categories are budgeted (expense semantics per spec §8).
 *   - Only LEAF nodes are editable; parents display the subtree sum.
 *   - Status tokens for progress: ≥95% = --neg (red),
 *     ≥80% = --warn (amber), else --pos / accent (green).
 *   - CSS Modules + tokens only — no inline hex.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useFinanceCategories,
  useFinanceBudgets,
  useSetBudgets,
  useCopyBudgets,
  useBudgetVsActual,
  type BudgetVsActualRow,
} from '../../api/finance';
import {
  formatEur,
  financeErrorMessage,
  buildCategoryTree,
  type CategoryTreeNode,
} from './finance-format';
import styles from './finance.module.css';
import budgetStyles from './Budgets.module.css';

// ─── Month navigation helpers ─────────────────────────────────────────────────

function currentYearMonth(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function stepMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  const ny = d.getFullYear();
  const nm = String(d.getMonth() + 1).padStart(2, '0');
  return `${ny}-${nm}`;
}

function prevMonthOf(ym: string): string {
  return stepMonth(ym, -1);
}

// ─── Expense category tree node ────────────────────────────────────────────────

/** Alias for the shared tree node — keeps render code unchanged. */
type BudgetTreeNode = CategoryTreeNode;

/** Leaf nodes = nodes with no children in the expense tree. */
function isLeaf(node: BudgetTreeNode): boolean {
  return node.children.length === 0;
}

// ─── MonthNavigator ───────────────────────────────────────────────────────────

function MonthNavigator({
  month,
  onChange,
}: {
  month: string;
  onChange: (m: string) => void;
}) {
  return (
    <div className={budgetStyles.monthNav}>
      <button
        type="button"
        className={`${styles.iconBtn} ${budgetStyles.navBtn}`}
        onClick={() => onChange(stepMonth(month, -1))}
        aria-label="Previous month"
      >
        ←
      </button>
      <span className={budgetStyles.monthLabel}>{month}</span>
      <button
        type="button"
        className={`${styles.iconBtn} ${budgetStyles.navBtn}`}
        onClick={() => onChange(stepMonth(month, 1))}
        aria-label="Next month"
      >
        →
      </button>
    </div>
  );
}

// ─── Edit Budgets tab ─────────────────────────────────────────────────────────

function EditBudgetsTab({
  month,
  onMonthChange,
}: {
  month: string;
  onMonthChange: (m: string) => void;
}) {
  const categoriesQ = useFinanceCategories({ includeInactive: false });
  const budgetsQ = useFinanceBudgets(month);
  const setBudgets = useSetBudgets();
  const copyBudgets = useCopyBudgets();

  const categories = useMemo(() => categoriesQ.data ?? [], [categoriesQ.data]);
  const serverBudgets = useMemo(() => budgetsQ.data ?? [], [budgetsQ.data]);

  const tree = useMemo(
    () => buildCategoryTree(categories, { kind: 'expense', excludeArchived: true }),
    [categories],
  );

  // Local edits keyed by categoryId → string value
  const [localAmounts, setLocalAmounts] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<number | null>(null);

  // Budget map: categoryId → amountEur from server
  const serverMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of serverBudgets) m.set(b.categoryId, b.amountEur);
    return m;
  }, [serverBudgets]);

  // When month or server data changes, reset local state
  const [seenKey, setSeenKey] = useState('');
  const cacheKey = `${month}:${serverBudgets.length}`;
  if (cacheKey !== seenKey) {
    setSeenKey(cacheKey);
    setLocalAmounts({});
    setIsDirty(false);
    setSaveError(null);
    setCopyError(null);
    setCopySuccess(null);
  }

  /** Current effective amount for a leaf node (local edit > server > 0). */
  const effectiveAmount = useCallback(
    (catId: string): number => {
      if (localAmounts[catId] !== undefined) {
        const v = parseFloat(localAmounts[catId]);
        return isNaN(v) ? 0 : Math.max(0, v);
      }
      return serverMap.get(catId) ?? 0;
    },
    [localAmounts, serverMap],
  );

  /** Compute subtree sum (used for parent display rows). */
  const subtreeSum = useCallback(
    (node: BudgetTreeNode): number => {
      if (isLeaf(node)) return effectiveAmount(node.cat.id);
      return node.children.reduce((sum, child) => sum + subtreeSum(child), 0);
    },
    [effectiveAmount],
  );

  const handleInput = (catId: string, value: string) => {
    setLocalAmounts((prev) => ({ ...prev, [catId]: value }));
    setIsDirty(true);
    setSaveError(null);
  };

  const handleSave = () => {
    // Collect leaf nodes, skipping those that are 0 AND have no server budget
    // (they were never budgeted — sending 0 would be a no-op DELETE that
    // produces unnecessary event noise). Leaves that ARE in serverMap are
    // still included even at 0 so the server can delete the existing budget.
    const entries: { categoryId: string; amountEur: number }[] = [];
    const collectLeaves = (nodes: BudgetTreeNode[]) => {
      for (const node of nodes) {
        if (isLeaf(node)) {
          const catId = node.cat.id;
          const amt = effectiveAmount(catId);
          if (amt > 0 || serverMap.has(catId)) {
            entries.push({ categoryId: catId, amountEur: amt });
          }
        } else {
          collectLeaves(node.children);
        }
      }
    };
    collectLeaves(tree);

    // Nothing to send — all leaves are 0 and none were ever budgeted.
    if (entries.length === 0) return;

    setBudgets.mutate(
      { month, entries },
      {
        onSuccess: () => {
          setLocalAmounts({});
          setIsDirty(false);
          setSaveError(null);
        },
        onError: (err) => {
          setSaveError(financeErrorMessage(err) ?? 'Save failed.');
        },
      },
    );
  };

  const handleDiscard = () => {
    setLocalAmounts({});
    setIsDirty(false);
    setSaveError(null);
  };

  const handleCopyFromPrev = () => {
    const fromMonth = prevMonthOf(month);
    setCopyError(null);
    setCopySuccess(null);
    copyBudgets.mutate(
      { fromMonth, toMonth: month },
      {
        onSuccess: (result) => {
          setCopySuccess(result.copied);
        },
        onError: (err) => {
          setCopyError(financeErrorMessage(err) ?? 'Copy failed.');
        },
      },
    );
  };

  const hasAnyBudget = serverBudgets.length > 0;
  const loading = categoriesQ.isLoading || budgetsQ.isLoading;

  const renderNode = (node: BudgetTreeNode): React.ReactNode => {
    const leaf = isLeaf(node);
    const amount = leaf ? effectiveAmount(node.cat.id) : subtreeSum(node);
    const localVal = localAmounts[node.cat.id];

    return (
      <div key={node.cat.id}>
        <div
          className={`${budgetStyles.budgetRow} ${node.depth === 1 ? budgetStyles.budgetRowRoot : node.depth === 2 ? budgetStyles.budgetRowMid : budgetStyles.budgetRowLeaf}`}
          style={{ paddingLeft: 12 + (node.depth - 1) * 20 }}
        >
          <span className={budgetStyles.budgetCatName}>{node.cat.name}</span>
          <span className={budgetStyles.budgetSpacer} />
          {leaf ? (
            <input
              type="number"
              min="0"
              step="0.01"
              className={budgetStyles.budgetInput}
              value={localVal !== undefined ? localVal : (amount > 0 ? String(amount) : '')}
              placeholder="0.00"
              onChange={(e) => handleInput(node.cat.id, e.target.value)}
            />
          ) : (
            <span className={`${budgetStyles.budgetParentAmt} ${styles.num}`}>
              {amount > 0 ? formatEur(amount) : <span className={styles.muted}>—</span>}
            </span>
          )}
        </div>
        {node.children.map((child) => renderNode(child))}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Budgets</h2>
          <div className={styles.subtitle}>
            Set monthly spending targets per expense category. Parent categories show
            the sum of their subtree.
          </div>
        </div>
        <div className={styles.actions}>
          <MonthNavigator month={month} onChange={onMonthChange} />
        </div>
      </header>

      {loading ? (
        <div className={styles.muted}>Loading…</div>
      ) : (categoriesQ.isError || budgetsQ.isError) ? (
        <div className={styles.error}>
          {financeErrorMessage(categoriesQ.error ?? budgetsQ.error)}
        </div>
      ) : tree.length === 0 ? (
        <div className={styles.empty}>
          No expense categories yet. Add them in Categories first.
        </div>
      ) : (
        <>
          {/* Copy-forward banner — shown when current month has no budgets */}
          {!hasAnyBudget && !isDirty && (
            <div className={budgetStyles.copyBanner}>
              <span>
                No budgets for <strong>{month}</strong> yet.
              </span>
              <button
                type="button"
                className="kos-btn kos-btn-primary"
                disabled={copyBudgets.isPending}
                onClick={handleCopyFromPrev}
              >
                {copyBudgets.isPending
                  ? 'Copying…'
                  : `Copy from ${prevMonthOf(month)}`}
              </button>
              {copyError && <span className={styles.muted}>{copyError}</span>}
              {copySuccess !== null && (
                <span className={budgetStyles.copyHint}>
                  {copySuccess === 0
                    ? 'Nothing to copy (previous month is also empty).'
                    : `Copied ${copySuccess} budget${copySuccess !== 1 ? 's' : ''}.`}
                </span>
              )}
            </div>
          )}

          {/* Budget tree */}
          <div className={`${styles.tableWrap} ${budgetStyles.budgetTree}`}>
            {tree.map((root) => renderNode(root))}
          </div>

          {/* Explicit Save bar — always visible when dirty */}
          {isDirty && (
            <div className={budgetStyles.saveBar}>
              {saveError && <span className={styles.muted}>{saveError}</span>}
              <button
                type="button"
                className="kos-btn"
                onClick={handleDiscard}
                disabled={setBudgets.isPending}
              >
                Discard
              </button>
              <button
                type="button"
                className="kos-btn kos-btn-primary"
                onClick={handleSave}
                disabled={setBudgets.isPending}
              >
                {setBudgets.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Budget vs Actual tree (for the vs-actual tab) ────────────────────────────

interface BvaNode {
  row: BudgetVsActualRow;
  children: BvaNode[];
}

function buildBvaTree(rows: BudgetVsActualRow[]): BvaNode[] {
  const byId = new Map<string, BvaNode>();
  for (const row of rows) byId.set(row.categoryId, { row, children: [] });

  const roots: BvaNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.row.parentId;
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  const sortRec = (nodes: BvaNode[]) => {
    nodes.sort((a, b) => a.row.name.localeCompare(b.row.name));
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

function ProgressBar({ pct }: { pct: number }) {
  const capped = Math.min(Math.max(pct, 0), 100);
  const cls =
    capped >= 95
      ? budgetStyles.progressRed
      : capped >= 80
        ? budgetStyles.progressAmber
        : budgetStyles.progressGreen;

  return (
    <div className={budgetStyles.progressTrack}>
      <div
        className={`${budgetStyles.progressFill} ${cls}`}
        style={{ width: `${capped}%` }}
      />
    </div>
  );
}

function BvaRows({
  nodes,
  expanded,
  toggle,
  depth,
}: {
  nodes: BvaNode[];
  expanded: Set<string>;
  toggle: (id: string) => void;
  depth: number;
}) {
  return (
    <>
      {nodes.map((node) => {
        const { row, children } = node;
        const hasChildren = children.length > 0;
        const isOpen = expanded.has(row.categoryId);
        const noBudget = row.budget === 0 && row.actual > 0;
        const pct = row.budget > 0 ? (row.actual / row.budget) * 100 : 0;
        const varClass =
          row.variance >= 0 ? budgetStyles.bvaPos : budgetStyles.bvaNeg;

        return (
          <div key={row.categoryId}>
            <div
              className={`${budgetStyles.bvaRow} ${depth === 0 ? budgetStyles.bvaRowRoot : ''} ${noBudget ? budgetStyles.bvaRowNoBudget : ''}`}
              style={{ paddingLeft: 12 + depth * 20 }}
            >
              {/* Expand toggle */}
              <div className={budgetStyles.bvaName}>
                {hasChildren ? (
                  <button
                    type="button"
                    className={budgetStyles.bvaTwisty}
                    onClick={() => toggle(row.categoryId)}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                  >
                    {isOpen ? '▾' : '▸'}
                  </button>
                ) : (
                  <span className={budgetStyles.bvaTwistySpacer} />
                )}
                <span>{row.name}</span>
                {noBudget && (
                  <span className={`${styles.badge} ${budgetStyles.badgeNoBudget}`}>
                    no budget
                  </span>
                )}
              </div>
              <span className={`${styles.num} ${budgetStyles.bvaCell}`}>
                {row.budget > 0 ? formatEur(row.budget) : <span className={styles.muted}>—</span>}
              </span>
              <span className={`${styles.num} ${budgetStyles.bvaCell}`}>
                {row.actual > 0 ? formatEur(row.actual) : <span className={styles.muted}>—</span>}
              </span>
              <span className={`${styles.num} ${budgetStyles.bvaCell} ${varClass}`}>
                {row.budget > 0 ? formatEur(row.variance) : <span className={styles.muted}>—</span>}
              </span>
              <div className={`${budgetStyles.bvaCell} ${budgetStyles.bvaProgress}`}>
                {row.budget > 0 && <ProgressBar pct={pct} />}
              </div>
            </div>
            {hasChildren && isOpen && (
              <BvaRows
                nodes={children}
                expanded={expanded}
                toggle={toggle}
                depth={depth + 1}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function BudgetVsActualTab({
  month,
  onMonthChange,
}: {
  month: string;
  onMonthChange: (m: string) => void;
}) {
  const bvaQ = useBudgetVsActual(month);
  const rows = useMemo(() => bvaQ.data ?? [], [bvaQ.data]);
  const tree = useMemo(() => buildBvaTree(rows), [rows]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Reset expanded state when the user navigates to a different month.
  useEffect(() => {
    setExpanded(new Set());
  }, [month]);

  // Auto-expand root nodes once data arrives (runs after render, so it cannot
  // conflict with "Collapse all" — collapsAll sets state, this effect only fires
  // when `tree` identity changes, i.e. when data reloads for a new month).
  useEffect(() => {
    if (tree.length === 0) return;
    const rootIds = tree.map((n) => n.row.categoryId);
    setExpanded(new Set(rootIds));
  }, [tree]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = () =>
    setExpanded(new Set(rows.map((r) => r.categoryId)));
  const collapseAll = () => setExpanded(new Set());

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Budget vs Actual</h2>
          <div className={styles.subtitle}>
            Spending against your monthly budgets. Progress bar turns amber at 80%,
            red at 95%. Rows flagged "no budget" have spend but no target set.
          </div>
        </div>
        <div className={styles.actions}>
          <MonthNavigator month={month} onChange={onMonthChange} />
        </div>
      </header>

      {bvaQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : bvaQ.isError ? (
        <div className={styles.error}>{financeErrorMessage(bvaQ.error)}</div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>
          No expense data for <strong>{month}</strong>. Import transactions or set
          budgets to see this view.
        </div>
      ) : (
        <>
          <div className={budgetStyles.bvaToolbar}>
            <button type="button" className={styles.iconBtn} onClick={expandAll}>
              Expand all
            </button>
            <button type="button" className={styles.iconBtn} onClick={collapseAll}>
              Collapse all
            </button>
          </div>
          <div className={`${styles.tableWrap} ${budgetStyles.bvaTable}`}>
            {/* Header */}
            <div className={budgetStyles.bvaHeader}>
              <div className={budgetStyles.bvaNameCol}>Category</div>
              <div className={`${styles.num} ${budgetStyles.bvaCell}`}>Budget</div>
              <div className={`${styles.num} ${budgetStyles.bvaCell}`}>Actual</div>
              <div className={`${styles.num} ${budgetStyles.bvaCell}`}>Variance</div>
              <div className={`${budgetStyles.bvaCell} ${budgetStyles.bvaProgress}`}>
                Spending
              </div>
            </div>
            <BvaRows nodes={tree} expanded={expanded} toggle={toggle} depth={0} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Budgets screen (two tabs, shared month state) ───────────────────────

type BudgetTab = 'edit' | 'vs-actual';

export function FinanceBudgetsScreen() {
  const [activeTab, setActiveTab] = useState<BudgetTab>('edit');
  const [month, setMonth] = useState(() => currentYearMonth());

  const tabs: { id: BudgetTab; label: string }[] = [
    { id: 'edit', label: 'Edit budgets' },
    { id: 'vs-actual', label: 'vs Actual' },
  ];

  return (
    <div>
      {/* Tab bar */}
      <div className={budgetStyles.tabBar}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`${budgetStyles.tab} ${activeTab === tab.id ? budgetStyles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content — month state is shared so navigating in one tab keeps
          the same month in the other */}
      {activeTab === 'edit' ? (
        <EditBudgetsTab month={month} onMonthChange={setMonth} />
      ) : (
        <BudgetVsActualTab month={month} onMonthChange={setMonth} />
      )}
    </div>
  );
}
